<template>
  <div class="p-3">
    <h4>Indeed — Postings</h4>
    <BForm inline class="mb-3">
      <BFormInput
        v-model="filters.search"
        @input="debouncedSearch"
        placeholder="Titre / Offer ID / Job posting ID / Company"
        class="mr-2"
      />
      <BFormSelect
        v-model="filters.status"
        @change="applyFilters"
        :options="statusOptions"
        class="mr-2"
      />
      <BFormSelect
        v-model="filters.offer_status"
        @change="applyFilters"
        :options="offerStatusOptions"
        class="mr-2"
      />
      <BFormInput
        v-model="filters.source_name"
        @input="debouncedSearch"
        placeholder="sourceName exact"
        class="mr-2"
      />
      <BFormSelect
        v-model="filters.lang"
        @change="applyFilters"
        :options="langOptions"
        class="mr-2"
      />
      <BFormCheckbox
        v-model="filters.stuck"
        @change="applyFilters"
        class="mr-2"
      >
        Bloqués (posted + offre closed)
      </BFormCheckbox>
    </BForm>

    <div class="d-flex align-items-center mb-2">
      <BButton
        size="sm"
        variant="danger"
        :disabled="!selectedIds.length || isBulkRunning"
        @click="bulkExpire"
      >
        <i class="fas fa-ban"></i>
        Forcer l'expiration ({{ selectedIds.length }})
      </BButton>
      <BButton
        v-if="selectedIds.length"
        size="sm"
        variant="link"
        :disabled="isBulkRunning"
        @click="selectedIds = []"
      >
        Tout désélectionner
      </BButton>
      <span v-if="bulkProgress" class="ml-2 text-muted">{{
        bulkProgress
      }}</span>
    </div>

    <BTable
      :items="items"
      :fields="fields"
      :busy="isBusy"
      small
      responsive
      striped
    >
      <div slot="table-busy" class="text-center my-2">
        <BSpinner class="align-middle"></BSpinner>
      </div>
      <template #head(selected)>
        <BFormCheckbox
          :checked="areAllSelectableSelected"
          :disabled="!selectableItems.length || isBulkRunning"
          @change="toggleSelectAll"
        ></BFormCheckbox>
      </template>
      <template #cell(selected)="{ item }">
        <BFormCheckbox
          :checked="selectedIds.includes(item.id)"
          :disabled="!canExpire(item) || isBulkRunning"
          @change="toggleRow(item)"
        ></BFormCheckbox>
      </template>
      <template #cell(title)="{ item }">
        <a
          :href="offerHref(item.offer_id)"
          target="_blank"
          rel="noopener"
          class="d-inline-block text-truncate align-bottom"
          style="max-width: 320px"
          :title="item.title || 'Ouvrir l\'offre'"
          >{{ item.title || "—" }}</a
        >
        <!-- ES-1624: a multi-city offer has one posting per city. -->
        <div
          v-if="item.city_slug"
          class="small text-muted text-truncate"
          style="max-width: 320px"
          translate="no"
        >
          {{ item.city_slug }}
        </div>
      </template>
      <template #cell(status)="{ item }">
        <BBadge :variant="statusVariant(item.status)">{{ item.status }}</BBadge>
      </template>
      <template #cell(offer_status)="{ item }">
        <BBadge :variant="offerStatusVariant(item.offer_status)">{{
          item.offer_status || "—"
        }}</BBadge>
      </template>
      <template #cell(source_name)="{ item }">
        {{ item.source_name || "—" }}
      </template>
      <template #cell(force_expired_at)="{ item }">
        <BBadge v-if="wasForcedRecently(item)" variant="warning">{{
          formatForcedAt(item)
        }}</BBadge>
        <span v-else>{{ formatForcedAt(item) }}</span>
      </template>
      <template #cell(indeed_pivot_status)="{ item }">
        {{ item.indeed_pivot_status || "—" }}
      </template>
      <template #cell(actions)="{ item }">
        <BButton
          size="sm"
          variant="outline-danger"
          :disabled="!canExpire(item)"
          :title="
            isAlreadyExpired(item)
              ? 'Déjà expired chez nous — renvoyer l\'expiration à Indeed'
              : 'Expirer ce posting sur Indeed'
          "
          @click="expire(item)"
        >
          <i class="fas fa-ban"></i>
          {{ isAlreadyExpired(item) ? "Forcer" : "Expire" }}
        </BButton>
      </template>
    </BTable>

    <BPagination
      :disabled="isBusy"
      v-model="currentPage"
      @input="fetch"
      :total-rows="total"
      :per-page="perPage"
      align="center"
    ></BPagination>
  </div>
</template>

<script>
import {
  BForm,
  BFormInput,
  BFormSelect,
  BFormCheckbox,
  BButton,
  BTable,
  BBadge,
  BSpinner,
  BPagination
} from "bootstrap-vue";
import debounce from "lodash/debounce";

export default {
  name: "IndeedPostings",
  components: {
    BForm,
    BFormInput,
    BFormSelect,
    BFormCheckbox,
    BButton,
    BTable,
    BBadge,
    BSpinner,
    BPagination
  },
  data() {
    return {
      filters: {
        search: null,
        source_name: null,
        status: null,
        offer_status: null,
        lang: null,
        stuck: false
      },
      items: [],
      currentPage: 1,
      perPage: 25,
      total: 0,
      isBusy: true,
      statusOptions: [
        { value: null, text: "Statut Indeed (tous)" },
        "pending",
        "posted",
        "expired",
        "error"
      ],
      offerStatusOptions: [
        { value: null, text: "Statut offre (tous)" },
        "open",
        "paused",
        "closed"
      ],
      langOptions: [{ value: null, text: "Langue (toutes)" }, "fr", "en"],
      selectedIds: [],
      // Chaque ligne = un appel GraphQL Indeed : on découpe pour rester loin des
      // timeouts HTTP, quitte à faire plusieurs requêtes. Plafond serveur : 100.
      bulkChunkSize: 20,
      isBulkRunning: false,
      bulkProgress: null,
      fields: [
        { key: "selected", label: "" },
        "id",
        { key: "offer_id", label: "Offer" },
        { key: "company_name", label: "Company" },
        { key: "title", label: "Titre" },
        { key: "lang", label: "Lang" },
        { key: "job_posting_id", label: "Job posting ID" },
        { key: "source_name", label: "sourceName" },
        { key: "status", label: "Indeed" },
        { key: "offer_status", label: "Offre" },
        { key: "indeed_pivot_status", label: "Réseau (Indeed)" },
        { key: "last_http_code", label: "HTTP" },
        { key: "last_synced_at", label: "Sync" },
        { key: "force_expired_at", label: "Forcé le" },
        { key: "actions", label: "Actions" }
      ]
    };
  },
  computed: {
    // Les lignes sans source_posting_id n'ont rien à envoyer à Indeed : non sélectionnables.
    selectableItems() {
      return this.items.filter(this.canExpire);
    },
    areAllSelectableSelected() {
      return (
        this.selectableItems.length > 0 &&
        this.selectableItems.every(item => this.selectedIds.includes(item.id))
      );
    }
  },
  methods: {
    statusVariant(s) {
      return (
        {
          posted: "success",
          expired: "secondary",
          error: "danger",
          pending: "warning"
        }[s] || "light"
      );
    },
    offerStatusVariant(s) {
      return (
        { open: "success", paused: "warning", closed: "danger" }[s] || "light"
      );
    },
    // Plain <a> + resolved href rather than <router-link>: the offer page must open
    // in a new tab so the current filtered list is not lost (same pattern as
    // FeedCompanies.vue).
    offerHref(offerId) {
      return this.$router.resolve({ name: "offer", params: { offerId } }).href;
    },
    isAlreadyExpired(item) {
      return item.status === "expired";
    },
    minutesSinceForced(item) {
      if (!item.force_expired_at) return null;
      return Math.floor(
        (Date.now() - new Date(item.force_expired_at).getTime()) / 60000
      );
    },
    // Mis en évidence juste après l'action : c'est la fenêtre où on se demande
    // « est-ce que mon clic a fait quelque chose ? ».
    wasForcedRecently(item) {
      const mins = this.minutesSinceForced(item);
      return mins !== null && mins < 10;
    },
    formatForcedAt(item) {
      const mins = this.minutesSinceForced(item);
      if (mins === null) return "—";
      if (mins < 1) return "à l'instant";
      if (mins < 60) return `il y a ${mins} min`;
      return new Date(item.force_expired_at).toLocaleString("fr-CA", {
        dateStyle: "short",
        timeStyle: "short"
      });
    },
    canExpire(item) {
      // Une ligne déjà `expired` chez nous reste actionnable : le statut local peut
      // être désynchronisé de Indeed (offre toujours en ligne) → force-expire.
      return !!item.source_posting_id;
    },
    toggleRow(item) {
      const i = this.selectedIds.indexOf(item.id);
      if (i === -1) {
        this.selectedIds.push(item.id);
      } else {
        this.selectedIds.splice(i, 1);
      }
    },
    toggleSelectAll() {
      // Ne porte que sur la page courante — la sélection reste explicite.
      const pageIds = this.selectableItems.map(item => item.id);
      this.selectedIds = this.areAllSelectableSelected
        ? this.selectedIds.filter(id => !pageIds.includes(id))
        : [...new Set([...this.selectedIds, ...pageIds])];
    },
    applyFilters() {
      this.currentPage = 1;
      // Un changement de filtre rendrait la sélection invisible et donc trompeuse.
      this.selectedIds = [];
      this.fetch();
    },
    fetch() {
      this.isBusy = true;
      this.$http
        .get("/enroll_admin/indeed-postings", {
          params: {
            page: this.currentPage,
            per_page: this.perPage,
            "filters[search]": this.filters.search || undefined,
            "filters[source_name]": this.filters.source_name || undefined,
            "filters[status]": this.filters.status || undefined,
            "filters[offer_status]": this.filters.offer_status || undefined,
            "filters[lang]": this.filters.lang || undefined,
            "filters[stuck]": this.filters.stuck ? 1 : undefined
          }
        })
        .then(({ data }) => {
          this.currentPage = data.current_page;
          this.total = data.total;
          this.items = data.data;
        })
        .finally(() => (this.isBusy = false));
    },
    expire(item) {
      const force = this.isAlreadyExpired(item);
      const message = force
        ? `Le posting #${item.id} (offer ${item.offer_id}) est déjà "expired" chez nous, mais peut être encore en ligne sur Indeed.\n\nRenvoyer l'expiration à Indeed ?`
        : `Expire le posting #${item.id} (offer ${item.offer_id}) sur Indeed ?`;
      if (!confirm(message)) return;
      this.$http
        .post(`/enroll_admin/indeed-postings/${item.id}/expire`, { force })
        .then(({ data }) => {
          // Sans retour explicite, un clic réussi ne se voyait qu'au rechargement
          // silencieux de la ligne. `error` = Indeed a refusé, HTTP 200 côté API.
          const failed = data && data.data && data.data.status === "error";
          this.$bvToast.toast(
            failed
              ? `Indeed a refusé l'expiration du posting #${item.id}. Voir Indeed Logs.`
              : `Posting #${item.id} expiré sur Indeed.`,
            {
              title: failed ? "Échec" : "Expiration forcée",
              variant: failed ? "warning" : "success",
              solid: true,
              noAutoHide: failed
            }
          );
          this.fetch();
        })
        .catch(() =>
          alert("Échec de l'expiration sur Indeed. Voir Indeed Logs.")
        );
    },
    async bulkExpire() {
      const ids = [...this.selectedIds];
      const selectedItems = this.items.filter(item => ids.includes(item.id));
      // Même règle qu'en unitaire : on ne force que si la sélection contient des
      // lignes déjà expired chez nous (statut local potentiellement désynchronisé).
      const force = selectedItems.some(this.isAlreadyExpired);
      const forcedCount = selectedItems.filter(this.isAlreadyExpired).length;

      const warning = force
        ? `\n\nDont ${forcedCount} déjà "expired" chez nous : l'expiration leur sera renvoyée quand même.`
        : "";
      if (
        !confirm(
          `Expirer ${ids.length} posting(s) sur Indeed ?${warning}\n\nUn appel Indeed par ligne, traité par lots de ${this.bulkChunkSize}.`
        )
      )
        return;

      this.isBulkRunning = true;
      const total = { requested: 0, expired: 0, skipped: 0, failed: 0 };
      try {
        for (let i = 0; i < ids.length; i += this.bulkChunkSize) {
          const chunk = ids.slice(i, i + this.bulkChunkSize);
          this.bulkProgress = `Traitement ${i + 1}–${i + chunk.length} sur ${
            ids.length
          }…`;
          const {
            data
          } = await this.$http.post(
            "/enroll_admin/indeed-postings/expire-bulk",
            { ids: chunk, force }
          );
          Object.keys(total).forEach(k => (total[k] += data.summary[k]));
        }
        this.selectedIds = [];
        this.$bvToast.toast(
          `${total.expired} expiré(s), ${total.skipped} ignoré(s), ${total.failed} en échec.`,
          {
            title: "Expiration en masse",
            variant: total.failed ? "warning" : "success",
            solid: true,
            noAutoHide: total.failed > 0
          }
        );
      } catch (e) {
        // Un lot qui échoue en entier (timeout, 5xx) : les lots déjà passés sont acquis.
        alert(
          `Lot interrompu après ${total.expired} expiration(s). Voir Indeed Logs, puis relancer sur les lignes restantes.`
        );
      } finally {
        this.isBulkRunning = false;
        this.bulkProgress = null;
        this.fetch();
      }
    }
  },
  created() {
    this.debouncedSearch = debounce(this.applyFilters, 300);
    this.fetch();
  }
};
</script>
